import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';

import { getPatientTreatments, updateTreatmentStage } from '../../../api/patients';
import type { PaginatedResponse, Patient, StageKind } from '../../../types/admin';
import { useAuth } from '../../auth/AuthProvider';
import { funnelColumnKey, type FunnelFilters } from './useFunnelColumn';

export type MovePatientStageArgs = {
  readonly patient: Patient;
  readonly fromStage: StageKind;
  readonly toStage: StageKind;
};

type ColumnData = InfiniteData<PaginatedResponse<Patient>>;

function removeFromColumn(data: ColumnData | undefined, patientId: string): ColumnData | undefined {
  if (!data) {
    return data;
  }
  let removed = false;
  const pages = data.pages.map((page) => {
    const items = page.items.filter((p) => {
      if (p.id === patientId) {
        removed = true;
        return false;
      }
      return true;
    });
    return { ...page, items };
  });
  if (!removed) {
    return data;
  }
  return {
    ...data,
    pages: pages.map((page, index) =>
      index === 0 ? { ...page, total: Math.max(0, page.total - 1) } : page,
    ),
  };
}

function addToColumn(data: ColumnData | undefined, patient: Patient): ColumnData | undefined {
  if (!data || data.pages.length === 0) {
    return data;
  }
  const already = data.pages.some((page) => page.items.some((p) => p.id === patient.id));
  if (already) {
    return data;
  }
  const [first, ...rest] = data.pages;
  return {
    ...data,
    pages: [{ ...first, items: [patient, ...first.items], total: first.total + 1 }, ...rest],
  };
}

/**
 * Moves a patient's latest cycle to a new step (`current_stage_kind`) by drag.
 *
 * The list payload does not carry treatment/cycle ids, so the cycle is resolved
 * client-side: the newest treatment (first in the API's `created_at desc` order)
 * and the cycle matching the card's `current_cycle_number` (or the highest cycle
 * number). The funnel column caches are updated optimistically and rolled back
 * on error; on settle every patient query is invalidated to reconcile.
 */
export function useMovePatientStage(filters: FunnelFilters) {
  const { tokens } = useAuth();
  const token = tokens?.access_token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ patient, toStage }: MovePatientStageArgs) => {
      if (!token) {
        throw new Error('Authenticated admin session is required.');
      }
      const treatments = await getPatientTreatments(patient.id, token);
      const latest = treatments[0];
      if (!latest) {
        throw new Error('Patient has no treatment to move.');
      }
      const cycles = latest.cycles ?? [];
      const cycle =
        cycles.find((c) => c.cycle_number === patient.current_cycle_number) ??
        [...cycles].sort((a, b) => b.cycle_number - a.cycle_number)[0];
      if (!cycle) {
        throw new Error('Patient has no treatment cycle to move.');
      }
      await updateTreatmentStage(
        patient.id,
        latest.id,
        cycle.id,
        { current_stage_kind: toStage },
        token,
      );
    },
    onMutate: async ({ patient, fromStage, toStage }) => {
      const fromKey = funnelColumnKey(fromStage, filters);
      const toKey = funnelColumnKey(toStage, filters);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: fromKey }),
        queryClient.cancelQueries({ queryKey: toKey }),
      ]);
      const prevFrom = queryClient.getQueryData<ColumnData>(fromKey);
      const prevTo = queryClient.getQueryData<ColumnData>(toKey);
      queryClient.setQueryData<ColumnData>(fromKey, (data) => removeFromColumn(data, patient.id));
      queryClient.setQueryData<ColumnData>(toKey, (data) =>
        addToColumn(data, { ...patient, current_stage_kind: toStage }),
      );
      return { prevFrom, prevTo, fromKey, toKey };
    },
    onError: (_error, _vars, context) => {
      if (!context) {
        return;
      }
      queryClient.setQueryData(context.fromKey, context.prevFrom);
      queryClient.setQueryData(context.toKey, context.prevTo);
    },
    onSettled: (_data, _error, { patient }) => {
      // ['patients'] partial-match also covers every ['patients','funnel',…] column.
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
      void queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      void queryClient.invalidateQueries({ queryKey: ['treatments', patient.id] });
    },
  });
}
